import { supabase } from '../config/supabaseClient';

/**
 * Sign up a new user
 * Wallet is automatically created by database trigger
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} fullName - User full name
 * @param {string} role - User role (1: User, 4: Admin)
 * @param {string} referralCode - Optional referral code from another user
 * @returns {Object} - { user, error }
 */
export const signUp = async (email, password, fullName, role, referralCode = '') => {
  try {
    const metadata = {
      full_name: fullName,
      role: role,
      role_id: parseInt(role),
      user_type: role === '4' ? 'admin' : 'user',
    };

    if (referralCode && referralCode.trim()) {
      metadata.referral_code = referralCode.trim().toUpperCase();
    }

    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: siteUrl,
      },
    });

    if (error) {
      // Handle specific error for duplicate email
      if (error.message.includes('already registered') || 
          error.message.includes('already been registered') ||
          error.message.includes('User already registered')) {
        return { user: null, error: 'An account with this email address already exists. Please sign in or use a different email.' };
      }
      return { user: null, error: error.message };
    }

    // Check if user was actually created (Supabase returns user even if email exists but not confirmed)
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      return { user: null, error: 'An account with this email address already exists. Please sign in or check your email for a verification link.' };
    }

    // Note: Wallet is now automatically created by database trigger (06_auto_create_wallet_trigger.sql)
    // No need to create it here - it will be initialized with real keys on first login
    console.log('User registered successfully. Wallet will be auto-created by database trigger.');

    return { user: data.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Sign in an existing user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Object} - { user, session, error }
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { user: null, session: null, error: error.message };
    }

    return { user: data.user, session: data.session, error: null };
  } catch (error) {
    return { user: null, session: null, error: error.message };
  }
};

/**
 * Sign out the current user
 * @returns {Object} - { error }
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      return { error: error.message };
    }

    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

/**
 * Check if an email exists in the system
 * @param {string} email - User email to check
 * @returns {Object} - { exists, error }
 */
export const checkEmailExists = async (email) => {
  try {
    // Attempt to sign in with the email and a dummy password
    // If the error is "Invalid login credentials", the email doesn't exist
    // If the error is something else, we can't determine
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: '___dummy___password___that___will___never___match___',
    });

    if (error) {
      // "Invalid login credentials" or "Email not confirmed" means email exists
      if (error.message.includes('Invalid login credentials')) {
        return { exists: true, error: null };
      }
      if (error.message.includes('Email not confirmed')) {
        return { exists: true, error: null };
      }
      // "User not found" or similar means email doesn't exist
      if (error.message.includes('not found')) {
        return { exists: false, error: null };
      }
      // For any other error, we can't determine - assume email exists for security
      return { exists: true, error: null };
    }

    // If no error (which shouldn't happen with dummy password), assume exists
    return { exists: true, error: null };
  } catch (error) {
    // On exception, assume exists for security
    return { exists: true, error: null };
  }
};

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Object} - { error }
 */
export const resetPassword = async (email) => {
  try {
    // First, check if the email exists in the system
    const { exists } = await checkEmailExists(email);
    
    if (!exists) {
      return { error: 'No account found with this email address. Please check your email or sign up for a new account.' };
    }

    // Proceed with password reset if email exists
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      // Handle specific Supabase errors
      if (error.message.includes('Email rate limit exceeded')) {
        return { error: 'Too many password reset attempts. Please wait a few minutes before trying again.' };
      }
      return { error: error.message };
    }
    
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

/**
 * Update user password
 * @param {string} newPassword - New password
 * @returns {Object} - { user, error }
 */
export const updatePassword = async (newPassword) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return { user: null, error: error.message };
    }

    return { user: data.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Get the current user session
 * @returns {Object} - { session, error }
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      return { session: null, error: error.message };
    }

    return { session: data.session, error: null };
  } catch (error) {
    return { session: null, error: error.message };
  }
};

/**
 * Get the current user
 * @returns {Object} - { user, error }
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) {
      return { user: null, error: error.message };
    }

    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

/**
 * Update the current user's display name
 * @param {string} fullName - New full name
 * @returns {Object} - { error }
 */
export const updateProfile = async (fullName) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: { full_name: fullName },
    });

    if (error) return { error: error.message };

    const { error: dbError } = await supabase
      .from('users')
      .update({ full_name: fullName })
      .eq('id', data.user.id);

    if (dbError) return { error: dbError.message };

    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};
